import * as React from 'react';
import {
  Placement,
  Side,
  Middleware,
  offset,
  flip,
  shift,
  arrow,
  FlipOptions,
  ShiftOptions
} from '@floating-ui/react';
import { floatingArrowSize } from '../patterns/00-atoms/floating-arrow/floating-arrow.tsx';
import { useCallback, useEffect } from 'react';
import { useEventListener } from './hooks/use-event-listener.ts';
import { useWindowEventServiceListener } from './hooks/use-window-event-service-listener.ts';
import { EventType } from '../services/window-event-service.ts';
import { useInterval } from './useInterval.ts';
import { safeWindow } from './window.ts';

/**
 * A floating element's static side is always the opposite of the element's placement to the reference element.
 * Required for e.g. the floating arrow's offset direction.
 * @param placement The floating content's placement.
 */
export const floatingContentStaticSide = (placement: Placement): Side => {
  switch (placement) {
    case 'top':
    case 'top-end':
    case 'top-start':
      return 'bottom';
    case 'right':
    case 'right-end':
    case 'right-start':
      return 'left';
    case 'bottom':
    case 'bottom-end':
    case 'bottom-start':
      return 'top';
    case 'left':
    case 'left-end':
    case 'left-start':
      return 'right';
  }
};

/**
 * For use in floating-ui.
 * All our floating elements are positioned in the same way.
 */
export const floatingMiddleware = (options: {
  arrowRef: React.RefObject<HTMLDivElement | null>;
  flipOptions?: FlipOptions;
  shiftOptions?: ShiftOptions;
}): Middleware[] => [
  offset({ mainAxis: floatingArrowSize + 7 }), // 7 is just a nice looking, well balanced distance.
  flip(options.flipOptions),
  shift({ padding: 10, ...options.shiftOptions }),
  arrow({
    element: options.arrowRef,
    padding: 15 // Arrow stops 15px from the edges of the floating element. This avoids overlapping the border radius.
  })
];

/**
 * Floating content, that is anchored to a reference element, must have its position updated on scroll and window resize.
 * You could also use floating-ui's autoUpdate feature, but it is harder to use for elements, that are always mounted and just hidden via css.
 * Also floating-ui's autoUpdate requires a ResizeObserver, which is unavailable in some browsers, like iOS 12.
 */
export const useUpdateFloatingPositionWhileOpen = (options: {
  open: boolean;
  update: () => void;
}) => {
  const { open, update } = options;

  const updateIfOpen = useCallback(() => {
    if (open) {
      update();
    }
  }, [open, update]);

  useEffect(updateIfOpen, [updateIfOpen]);

  // Could also use our throttled useWindowEventServiceListener, but it looks more fluid when its not throttled.
  // Floating-ui also doesn't throttle on scroll.
  useEventListener('scroll', () => updateIfOpen(), safeWindow(), { enabled: open });

  useWindowEventServiceListener(EventType.Resize, { listener: updateIfOpen }, { enabled: open });

  // In some places the reference element is moving irregularly (e.g. the tag input in create-question moves after every tag).
  useInterval(200, updateIfOpen, { enabled: open });
};
